import { BsModalRef } from 'ngx-bootstrap/modal'

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { AppState } from 'src/app/core/core.state'
import { Store } from '@ngrx/store'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'

import moment from 'moment-mini'
import {
  TimeBlock,
  TimeBlockDays,
  TimeBlockType
} from '../../time-block/time-block'
import { TimeBlockService } from '../../time-block/time-block.service'
import { catchError, take, timeout } from 'rxjs/operators'
import { timeBlockActions } from '../../time-block/time-block.actions'
import { Actions, ofType } from '@ngrx/effects'
import { Subscription, combineLatest, of, firstValueFrom } from 'rxjs'
import { NotificationService } from 'src/app/core/notification.service'
import { TranslatorService } from 'src/app/core/translator.service'
import { SelectableItem } from 'src/app/shared/pipes/map-to-selectable-items.pipe'
import { ConfigurationService } from '../../../shared/services/configuration.service'
import { TranslationService } from '../../translation/translation.service'

@Component({
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class TimeBlockCreateEditModal implements OnInit {
  @Input() productId?: number
  @Input() memberId?: number
  @Input() timeBlock: TimeBlock
  @Input() timeBlocks: TimeBlock[]
  @Output() updateTimeBlocks: EventEmitter<any> = new EventEmitter()
  public form: UntypedFormGroup
  private subscriptions: Subscription[] = []
  public multiSelectItems: SelectableItem[]
  public selectedItems: SelectableItem[] = []
  constructor(
    private bsModalRef: BsModalRef,
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private timeBlockService: TimeBlockService,
    private actions$: Actions,
    private notificationService: NotificationService,
    private translatorService: TranslatorService,
    private translationService: TranslationService,
    public configurationService: ConfigurationService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.setSelectMultipleComponentItems()

    this.form = this.fb.group({
      isAvailable: [0, [Validators.required]],
      startTime: [null, [Validators.required]],
      endTime: [null, [Validators.required]],
      price: [0, [Validators.min(0), Validators.max(999)]],
      days: [null, [Validators.required]]
    })
    if (this.timeBlock) {
      this.form.patchValue(this.timeBlock)
      this.timeBlock.days?.forEach((day) => {
        this.selectedItems.push(day)
      })
    }
  }

  public onSubmit() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })

      return
    }

    if (
      moment(this.form.value.endTime).valueOf() <=
      moment(this.form.value.startTime).valueOf()
    ) {
      this.notificationService.emit(
        'employeeModule.endTimeEarlierThanStartTime',
        'warning'
      )
      return
    }
    this.form.value.days = this.form.value.days.map(
      (dayLabel) => dayLabel.value
    )
    const formValues = this.form.value

    const updatedTimeBlocks = this.getUpdatedTimeBlocks(
      formValues,
      formValues.isAvailable,
      formValues.price
    )

    const completeWeekCalendar =
      this.timeBlockService.createCompleteWeekCalendar(updatedTimeBlocks)

    this.store.dispatch(
      timeBlockActions.updateOneByItemTypeAndId({
        itemId: this.productId ?? this.memberId,
        timeBlockType: this.productId
          ? TimeBlockType.PRODUCT
          : TimeBlockType.MEMBER,
        entities: completeWeekCalendar
      })
    )

    this.actions$
      .pipe(
        ofType(timeBlockActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          this.updateTimeBlocks.next(true)
          this.notificationService.emit('alerts.success', 'success')
          this.cancel()
        }
      })
  }

  public onClickDeleteBlock() {
    const startHourAndMinuteOfSelectedTimeBlock = moment(
      this.timeBlock.startTime
    ).format('HH:mm')

    const selectedTimeBlockStartTimeString = moment(this.timeBlock.startTime)
      .set({
        hour: Number(startHourAndMinuteOfSelectedTimeBlock.slice(0, 2)),
        minute: Number(startHourAndMinuteOfSelectedTimeBlock.slice(-2))
      })
      .format('HH:mm')

    const endHourAndMinuteOfSelectedTimeBlock = moment(
      this.timeBlock.endTime
    ).format('HH:mm')

    const selectedTimeBlockEndTimeString = moment(this.timeBlock.endTime)
      .set({
        hour: Number(endHourAndMinuteOfSelectedTimeBlock.slice(0, 2)),
        minute: Number(endHourAndMinuteOfSelectedTimeBlock.slice(-2))
      })
      .format('HH:mm')

    // needed because getOverlappedTimeBlock() works with string start and end times like '00:00', '12:59', etc..
    const timeBlockWithStringTimes = {
      ...this.timeBlock,
      startTime: selectedTimeBlockStartTimeString,
      endTime: selectedTimeBlockEndTimeString
    }

    const timeBlocksToDelete = this.getTimeBlocksWithinBlock(
      timeBlockWithStringTimes,
      this.form.value
    )

    const timeBlocksAfterDeletion = this.timeBlocks.filter(
      (timeBlock) => !timeBlocksToDelete.includes(timeBlock)
    )

    const completeWeekCalendar =
      this.timeBlockService.createCompleteWeekCalendar(
        timeBlocksAfterDeletion.filter(
          (timeBlock) =>
            (timeBlock.isAvailable && timeBlock.price) ||
            timeBlock.isAvailable == 0 ||
            !timeBlock.isAvailable
        )
      )

    this.store.dispatch(
      timeBlockActions.updateOneByItemTypeAndId({
        itemId: this.productId ?? this.memberId,
        timeBlockType: this.productId
          ? TimeBlockType.PRODUCT
          : TimeBlockType.MEMBER,
        entities: completeWeekCalendar
      })
    )

    this.actions$
      .pipe(
        ofType(timeBlockActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          this.store.dispatch(
            timeBlockActions.removeOne({ id: this.timeBlock.id })
          )
          this.updateTimeBlocks.next(true)
          this.notificationService.emit('alerts.success', 'success')
          this.cancel()
        }
      })
  }

  public cancel() {
    this.bsModalRef.hide()
  }

  private getOverlappedTimeBlock(timeBlockToFind: TimeBlock): TimeBlock {
    const overlappedTimeBlock = this.timeBlocks.filter((existingTimeBlock) => {
      // get overlapped blocks
      return (
        existingTimeBlock.productId == timeBlockToFind.productId &&
        existingTimeBlock.isoWeekDayNumber ==
          timeBlockToFind.isoWeekDayNumber &&
        Number(timeBlockToFind.startTime.replace(':', '')) <=
          Number(existingTimeBlock.endTime.replace(':', '')) &&
        Number(timeBlockToFind.endTime.replace(':', '')) >=
          Number(existingTimeBlock.startTime.replace(':', ''))
      )
    })

    return overlappedTimeBlock?.length ? overlappedTimeBlock[0] : null
  }

  private getTimeBlocksWithinBlock(
    timeBlockToFind: TimeBlock,
    formValues: any
  ): TimeBlock[] {
    return this.timeBlocks.filter((existingTimeBlock) => {
      if (existingTimeBlock.productId != timeBlockToFind.productId) {
        return false
      }

      const selectedDays = this.getSelectedDays(
        formValues.days?.map((day) => day.value)
      )
      if (!selectedDays.includes(existingTimeBlock.isoWeekDayNumber)) {
        return false
      }

      if (
        Number(timeBlockToFind.startTime.replace(':', '')) >
        Number(existingTimeBlock.startTime.replace(':', ''))
      ) {
        return false
      }

      if (
        Number(timeBlockToFind.endTime.replace(':', '')) <
        Number(existingTimeBlock.endTime.replace(':', ''))
      ) {
        return false
      }

      return true
    })
  }

  private getUpdatedTimeBlocks(
    formValues: any,
    isAvailable: boolean,
    price: number
  ): TimeBlock[] {
    let selectedDays = formValues.days

    if (formValues.days.includes(TimeBlockDays.EVERY_DAY)) {
      selectedDays = [
        TimeBlockDays.MONDAY,
        TimeBlockDays.TUESDAY,
        TimeBlockDays.WEDNESDAY,
        TimeBlockDays.THURSDAY,
        TimeBlockDays.FRIDAY,
        TimeBlockDays.SATURDAY,
        TimeBlockDays.SUNDAY
      ]
    }

    selectedDays.forEach((day) => {
      const timeBlockToCreate = {
        dayName: moment().day(day).format('dddd'),
        isoWeekDayNumber: day,
        isAvailable: isAvailable,
        price: price, // mark
        startTime: formValues.startTime
          ? moment(formValues.startTime).format('HH:mm').toString()
          : '00:00',
        endTime: formValues.endTime
          ? moment(formValues.endTime).format('HH:mm').toString()
          : '00:00',
        blockLengthInMinutes: Number(
          moment
            .duration(formValues.endTime.diff(formValues.startTime))
            .asMinutes()
            .toFixed(0)
        ),
        productId: this.productId
      }

      // check if there is an overlapping
      const overlappedTimeBlock = this.getOverlappedTimeBlock(timeBlockToCreate)
      if (overlappedTimeBlock) {
        const indexOfOverlappedTimeBlock =
          this.timeBlocks.indexOf(overlappedTimeBlock)

        console.log(
          `Found overlapping timeblocks and will overwrite ${JSON.stringify(
            this.timeBlocks[indexOfOverlappedTimeBlock]
          )} with timeblock ${JSON.stringify(timeBlockToCreate)}`
        )

        this.timeBlocks[indexOfOverlappedTimeBlock] = timeBlockToCreate
      } else {
        console.log(
          `Found updateable/creacteble timeblock ${JSON.stringify(
            timeBlockToCreate
          )}`
        )
        this.timeBlocks.push(timeBlockToCreate)
      }
    })

    // fill the gaps (available blocks) as we have to send a complete week calendar
    const sortedTimeBlocks = this.timeBlocks
      .sort(
        (a, b) =>
          Number(a.startTime.replace(':', '')) -
          Number(b.startTime.replace(':', ''))
      )
      .sort((a, b) => a.isoWeekDayNumber - b.isoWeekDayNumber)

    return sortedTimeBlocks
  }

  private getSelectedDays(days: number[]) {
    let selectedDays = days
    if (selectedDays.includes(TimeBlockDays.EVERY_DAY)) {
      selectedDays = [
        TimeBlockDays.MONDAY,
        TimeBlockDays.TUESDAY,
        TimeBlockDays.WEDNESDAY,
        TimeBlockDays.THURSDAY,
        TimeBlockDays.FRIDAY,
        TimeBlockDays.SATURDAY,
        TimeBlockDays.SUNDAY
      ]
    }

    return selectedDays
  }

  public async getAllWeekdayLabels(): Promise<string[]> {
    const dayKeys = [1, 2, 3, 4, 5, 6, 7]

    const allDays = (
      await firstValueFrom(
        this.translatorService
          .getManyTranslationByKeyObservable(['general.daily'])
          .pipe(take(1))
      )
    ).map((item) => item.label)

    const otherDays$ = dayKeys.map((day) =>
      this.configurationService.getWeekdayName(day, 'EEEE')
    )

    const allLabels$ = combineLatest([allDays, ...otherDays$])

    return firstValueFrom(allLabels$.pipe(take(1)))
  }

  private async setSelectMultipleComponentItems(): Promise<void> {
    const allWeekdays = await this.getAllWeekdayLabels()

    this.multiSelectItems = allWeekdays.map((label, index) => ({
      value: index,
      label: label
    }))

    if (this.timeBlock.days) {
      this.timeBlock.days = (this.timeBlock.days as number[]).map((dayKey) => {
        const selectable: SelectableItem = {
          value: dayKey,
          label: allWeekdays[dayKey]
        }
        return selectable
      })
    }
  }

  adjustSelectedDays(item: SelectableItem[]) {
    this.form.controls.days.patchValue(item)
  }

  ngOnDestroy() {
    this.subscriptions.map((s) => s.unsubscribe())
  }
}
